/**
 * Safe wrappers for DataDog RUM vitals.
 *
 * Uses `getDatadogRum()` from `./rumClient` (no app imports here). No-op when RUM was never
 * initialized (see `configureRum.ts`).
 *
 * Keep the module generic: feature-specific metric names and context mapping belong near the feature
 * code. This file can host wrappers for additional RUM vital APIs (for example numeric vitals) as
 * we start using them.
 */

import { getDatadogRum } from "./rumClient";

export type RumDurationVitalContextValue = string | number | boolean | null | undefined;
export type RumDurationVitalContext = Record<string, RumDurationVitalContextValue>;

export type RumDurationVitalPayload = {
    /** Duration vital name shown in DataDog. */
    name: string;
    /** Elapsed ms (e.g. from performance.now()); rounded to integer ms for RUM. */
    durationMs: number;
    /** Dimensions attached to the vital. DataDog RUM sanitizes this context before sending. */
    context?: RumDurationVitalContext;
};

/**
 * Records one DataDog RUM duration vital.
 *
 * Safe to call from application flows: never throws, so instrumentation cannot break UI behavior.
 */
export async function recordRumDurationVital(payload: RumDurationVitalPayload): Promise<void> {
    try {
        const datadogRum = await getDatadogRum();
        if (!datadogRum) {
            return;
        }

        const durationRounded = Math.round(payload.durationMs);
        const endEpoch = Date.now();
        datadogRum.addDurationVital(payload.name, {
            startTime: endEpoch - durationRounded,
            duration: durationRounded,
            context: payload.context,
        });
    } catch {
        // RUM must not affect application behavior.
    }
}
